import { useEffect, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import axios from 'axios';

export const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

/**
 * Reusable "type a GSTIN, auto-fill the rest" input. Knows nothing about Customer/Vendor field
 * names - on a successful lookup it calls `onResolved` with the plain resolved fields, and the
 * parent form decides what to do with them. Never blocks typing or submission: a not-found or
 * unavailable lookup just shows a muted note and leaves the rest of the form editable.
 */
function GstinLookupInput({ value, onChange, onResolved, name = 'taxId', disabled, idleHint = 'Printed on Sale Bill tax invoices' }) {
  const [status, setStatus] = useState('idle'); // idle | loading | found | not_found | invalid | unavailable
  const lastCheckedRef = useRef(null);

  const maybeVerify = async (raw) => {
    const gstin = (raw || '').trim().toUpperCase();
    if (!GSTIN_PATTERN.test(gstin)) {
      if (gstin.length > 0) {
        setStatus('invalid');
      }
      return;
    }
    if (lastCheckedRef.current === gstin) {
      return;
    }
    lastCheckedRef.current = gstin;
    setStatus('loading');
    try {
      const response = await axios.get(`/api/gstin/${gstin}`);
      const data = response.data;
      if (data.status === 'FOUND') {
        setStatus('found');
        if (onResolved) {
          onResolved({
            legalName: data.legalName,
            tradeName: data.tradeName,
            buildingNo: data.buildingNo,
            street: data.street,
            locality: data.locality,
            city: data.city,
            district: data.district,
            state: data.state,
            pincode: data.pincode,
            registrationDate: data.registrationDate,
            businessType: data.businessType,
            gstStatus: data.gstStatus
          });
        }
      } else if (data.status === 'NOT_FOUND') {
        setStatus('not_found');
      } else {
        setStatus('unavailable');
      }
    } catch (error) {
      setStatus('unavailable');
    }
  };

  useEffect(() => {
    const gstin = (value || '').trim().toUpperCase();
    if (GSTIN_PATTERN.test(gstin)) {
      maybeVerify(value);
    } else if (gstin.length >= 15) {
      // Typed a full-length code that still doesn't match the shape - flag it without waiting
      // for blur, since the user has committed to at least as many characters as a real GSTIN needs.
      if (status !== 'invalid') {
        setStatus('invalid');
      }
      lastCheckedRef.current = null;
    } else {
      // Still short of a full GSTIN (or field cleared) - not wrong yet, just incomplete.
      if (status !== 'idle') {
        setStatus('idle');
      }
      lastCheckedRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <>
      <div className="d-flex align-items-center gap-2">
        <Form.Control
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          onBlur={() => maybeVerify(value)}
          placeholder="Tax ID / GSTIN"
          disabled={disabled}
        />
        {status === 'loading' && <Spinner animation="border" size="sm" />}
      </div>
      {status === 'found' && (
        <Form.Text className="text-success">Verified with GST records</Form.Text>
      )}
      {status === 'not_found' && (
        <Form.Text className="text-muted">GSTIN not found - please enter details manually</Form.Text>
      )}
      {status === 'invalid' && (
        <Form.Text className="text-danger">
          Doesn't look like a valid GSTIN (expected a 15-character code, e.g. 27AAPFU0939F1ZV) - leave blank if none
        </Form.Text>
      )}
      {status === 'unavailable' && (
        <Form.Text className="text-muted">Couldn't verify right now - please enter details manually</Form.Text>
      )}
      {status === 'idle' && idleHint && (
        <Form.Text className="text-muted">{idleHint}</Form.Text>
      )}
    </>
  );
}

export default GstinLookupInput;
